import React from 'react';
import { PropTypes } from 'prop-types';
import cx from 'classnames';
import { Field } from 'react-final-form';
import { InputField, validations } from '@unite-us/app-components';
import {
  DateField,
  RadioField,
  DurationField,
} from '@unite-us/ui';
import { validateUnitAmount } from 'common/form/Interactions/utils';
import ErrorMessage from 'common/utils/ErrorMessage';
import {
  DATE_TYPE_VALUES,
  DATE_TYPE_OPTIONS,
  FORM_ERRORS,
} from '../../../utils/serviceAuthorizationUtils';

const hasAmountError = (error) => (
  error === FORM_ERRORS.AMOUNT_EXCEEDED ||
  error === FORM_ERRORS.AUTHORIZED_AMOUNT_BELOW_INVOICED
);

const costValidation = (value) => (
  validations.isRequired(value) ||
  validations.isGreaterThan(value, null, 0) ||
  validations.isLessThan(value, 'Should be less than $1,000,000', 1_000_000)
);

const DefaultAuthorizationFormFields = ({
  dateType,
  error,
  isUnitBased,
  isCostBased,
  feeScheduleProgram,
  amountLabel,
}) => (
  <>
    <Field
      name="date_type"
      type="select"
    >
      {(props) => (
        <RadioField
          className="mb-0"
          label="PERIOD OF SERVICE"
          options={DATE_TYPE_OPTIONS}
          {...props}
          required
        />
      )}
    </Field>
    {dateType === DATE_TYPE_VALUES.SINGLE_DATE && (
      <Field
        name="single_date"
        validate={(value) => validations.isRequired(value)}
      >
        {(fieldProps) => (
          <DateField
            {...fieldProps}
            className="mb-0"
            id="single_date"
            label="SERVICE DELIVERY DATE"
            required
          />
        )}
      </Field>
    )}
    {dateType === DATE_TYPE_VALUES.RANGE_DATE && (
      <Field name="range_date_start" validate={(value) => validations.isRequired(value)}>
        {(startProps) => (
          <Field name="range_date_end" validate={(value) => validations.isRequired(value)}>
            {(endProps) => (
              <DurationField
                className="mb-0"
                style={{ dropdown: { right: 0, left: 'unset' } }}
                id="duration_field"
                isUTC
                label="SERVICE DELIVERY DATES"
                startField={startProps}
                endField={endProps}
                required
              />
            )}
          </Field>
        )}
      </Field>
    )}
    {error === FORM_ERRORS.DATES_OUT_OF_RANGE && (
      <div className="flex col-span-2 space-x-2 px-0 mt-0 pb-4">
        <ErrorMessage>
          The authorized service delivery dates fall outside the
          client&apos;s enrollment dates for the associated social
          care coverage plan. Please change the authorized service
          delivery dates.
        </ErrorMessage>
      </div>
    )}
    {error === FORM_ERRORS.INVOICES_OUTSIDE_AUTH_DATES && (
      <div className="flex col-span-2 space-x-2 px-0 mt-0 pb-4">
        <ErrorMessage>
          This case has invoices with service delivery dates that fall outside
          the dates requested for authorization.
        </ErrorMessage>
      </div>
    )}
    {isUnitBased ? (
      <Field name="authorized_amount" validate={validateUnitAmount}>
        {(fieldProps) => (
          <InputField
            {...fieldProps}
            className={cx('mb-0', hasAmountError(error) && 'ui-input-field--has-error')}
            id="authorized_amount"
            label={amountLabel}
            required
            type="number"
          />
        )}
      </Field>
    ) : (
      <Field
        name="authorized_amount"
        validate={costValidation}
      >
        {(fieldProps) => (
          <InputField
            {...fieldProps}
            className={cx('mb-0', hasAmountError(error) && 'ui-input-field--has-error')}
            id="authorized_amount"
            label={amountLabel}
            placeholder="0.00"
            required
            step="0.1"
            type="number"
          />
        )}
      </Field>
    )}
    <div>
      <label className="block mb-4" htmlFor="service-authorization-unit-info">Unit</label>
      <p className="capitalize" id="service-authorization-unit-info">
        {isCostBased ? 'Dollars' : feeScheduleProgram.unit}
      </p>
    </div>

    {error === FORM_ERRORS.AMOUNT_EXCEEDED && (
      <div className="flex col-span-2 space-x-2 px-0 mt-0 pb-4">
        <ErrorMessage>
          The authorized amount is more than the available amount.
          Please adjust the authorized amount.
        </ErrorMessage>
      </div>
    )}

    {error === FORM_ERRORS.AUTHORIZED_AMOUNT_BELOW_INVOICED && (
      <div className="flex col-span-2 space-x-2 px-0 mt-0 pb-4">
        <ErrorMessage>
          The amount requested cannot be less than what has already been invoiced.
          Please adjust the amount requested.
        </ErrorMessage>
      </div>
    )}
  </>
);

DefaultAuthorizationFormFields.propTypes = {
  dateType: PropTypes.string.isRequired,
  error: PropTypes.string,
  isUnitBased: PropTypes.bool.isRequired,
  isCostBased: PropTypes.bool.isRequired,
  feeScheduleProgram: PropTypes.shape({
    unit: PropTypes.string,
  }).isRequired,
  amountLabel: PropTypes.string.isRequired,
};

DefaultAuthorizationFormFields.defaultProps = {
  error: undefined,
};

export default DefaultAuthorizationFormFields;
